import { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { LiveEventsList } from './LiveEventsList/LiveEventsList';
import { LiveBettingContext } from 'context/liveBettingContext';
import { LiveEvent } from 'types/LiveEvent';
import { usePlayerLayout } from 'lib/playerLayout';
import { sportTranslationKey } from 'helpers/sportI18n';
import { getDefaultSportIconSrc } from 'helpers/sportIcon';
import { SportIcon } from 'components/ThemeIcon/SportIcon';
import nav from 'components/mobile/ModernSportNav/ModernSportNav.module.css';
import { LiveMarket, LiveMarketKey, liveMarketsOn } from './liveMarkets';
import {
  getLiveBettingSportName,
  LIVE_FULL_TIME_BET_MAP,
  LIVE_SPORT_IDs,
} from 'config/constants';
import { translateBetMarketName } from 'helpers/betI18n';
import { SportIDs } from 'types/enums';
import type { TFunction } from 'i18next';
import { VipLiveHero } from './VipLiveHero/VipLiveHero';
import { LiveRowsSkeleton } from './LiveRowsSkeleton/LiveRowsSkeleton';
import styles from './LiveBetting.module.css';

/** An odds tab's name. The first one is the row's own market: 1X2 where a
 *  match can end in a draw, the winner elsewhere. */
export const tabLabel = (m: LiveMarket, sportId: number, t: TFunction) => {
  if (m.title) return m.title(t);
  if (m.feedName === 'curated')
    return translateBetMarketName({ name: m.label, odds: [] }, t);
  if (m.feedName)
    return translateBetMarketName({ name: m.feedName, odds: [] }, t);
  if (m.key !== 'full') return t(m.label);
  return [SportIDs.SOCCER, SportIDs.ICE_HOCKEY, SportIDs.HANDBALL].includes(
    sportId
  )
    ? '1X2'
    : t('bet_odd_winner');
};

export const LiveBetting = () => {
  const { t } = useTranslation();
  const {
    events,
    snapshot,
    favorites,
    liveCounts,
    liveReady,
    setWatchedSports,
  } = useContext(LiveBettingContext);
  const layout = usePlayerLayout();
  const modern = layout === 'modern';
  const [picked, setPicked] = useState<number | null>(null);
  const [market, setMarket] = useState<LiveMarketKey>('full');
  const favoriteEvents = Array.from(favorites.values()).flat() as LiveEvent[];

  // A favourite is listed on top and stays in its sport's list as well.
  // Before a list comes, what the page last showed of it (a quick refresh).
  const rowsOf = (sportId: number): LiveEvent[] =>
    events.get(sportId) ?? snapshot.get(sportId) ?? [];
  const hasRows = (sportId: number) =>
    events.has(sportId) || snapshot.has(sportId);
  // The rail stands from the first moment, on the counts, while the lists
  // load behind it.
  const countOf = (sportId: number): number =>
    events.has(sportId) ? rowsOf(sportId).length : liveCounts.get(sportId) ?? 0;
  const sportIds = Array.from(
    new Set<number>([...events.keys(), ...liveCounts.keys()])
  )
    .filter((sportId) => countOf(sportId) > 0)
    .sort((a, b) => sportRank(a) - sportRank(b));
  // One sport at a time, picked on the icon rail (both layouts; the
  // classic one in its own colours, as tipx365's mobile live).
  const current =
    picked !== null && sportIds.includes(picked) ? picked : sportIds[0];
  const shown = sportIds.filter((x) => x === current);
  // Which odds the rows show (1 X 2, double chance, next goal...). A tab
  // only for a market some match on the list has just now.
  const markets = liveMarketsOn(
    current,
    rowsOf(current),
    LIVE_FULL_TIME_BET_MAP.get(current)
  );
  const currentMarket = markets.find((m) => m.key === market) ?? markets[0];
  // Classic: null for every sport; a sport that left the list shows all.
  const [classicSport, setClassicSport] = useState<number | null>(null);
  // "All" grows downwards in the rail's order: a sport shows once those above
  // it have, so nothing already on screen is pushed down. The next one to come
  // holds its place with the outline of its rows.
  const firstWaiting = sportIds.findIndex((x) => !hasRows(x));
  const classicShown =
    classicSport !== null && sportIds.includes(classicSport)
      ? [classicSport]
      : firstWaiting < 0
        ? sportIds
        : sportIds.slice(0, firstWaiting + 1);

  // Classic "All" lists the sports under each other: the ones on screen now.
  const [onScreen, setOnScreen] = useState<number[]>([]);
  const sectionsKey = modern ? '' : classicShown.join(',');
  useEffect(() => {
    if (!sectionsKey || typeof IntersectionObserver === 'undefined') return;
    const visible = new Set<number>();
    const observer = new IntersectionObserver((seen) => {
      seen.forEach((x) => {
        const id = Number(x.target.id.replace('live-sport-', ''));
        if (x.isIntersecting) visible.add(id);
        else visible.delete(id);
      });
      setOnScreen(Array.from(visible));
    });
    sectionsKey.split(',').forEach((id) => {
      const section = document.getElementById(`live-sport-${id}`);
      if (section) observer.observe(section);
    });
    return () => observer.disconnect();
  }, [sectionsKey]);

  // Live updates only for what is on screen, and the favourites' sports; the
  // other sports refresh quietly behind it.
  const inFront: number[] = modern
    ? [current]
    : onScreen.length > 0
      ? onScreen
      : classicShown.slice(0, 1);
  const front = inFront.filter((x) => x !== undefined);
  const watched = Array.from(
    new Set<number>([
      // Before the rail's numbers are in, football: never every sport.
      ...(front.length > 0 ? front : [SportIDs.SOCCER]),
      ...(Array.from(favorites.keys()) as number[]),
    ])
  ).join(',');
  useEffect(() => {
    if (watched === '') return;
    setWatchedSports(watched.split(',').map(Number));
    return () => setWatchedSports(null);
  }, [watched, setWatchedSports]);

  const outlineOnly = sportIds.length === 0 && !liveReady;
  const heroEvents = new Map<number, LiveEvent[]>([...snapshot, ...events]);

  return (
    <>
      <div>
        {modern && sportIds.length > 0 && (
          <div className={modern ? nav.sports : styles.classicSports}>
            {sportIds.map((id) => (
              <button
                type="button"
                key={id}
                className={
                  modern
                    ? classNames(nav.sport, id === current && nav.sportActive)
                    : classNames(
                        styles.classicSport,
                        id === current && styles.classicSportOn
                      )
                }
                onClick={() => {
                  setPicked(id);
                  setMarket('full');
                  if (!modern)
                    document
                      .getElementById(`live-sport-${id}`)
                      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                <span
                  className={modern ? nav.iconWrap : styles.classicIconWrap}
                >
                  <SportIcon
                    sportId={id}
                    className={modern ? nav.icon : styles.classicIcon}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = getDefaultSportIconSrc();
                    }}
                  />
                  <span className={modern ? nav.count : styles.classicCount}>
                    {countOf(id)}
                  </span>
                </span>
                <span className={modern ? nav.name : styles.classicName}>
                  {t(sportTranslationKey(id), {
                    defaultValue: getLiveBettingSportName(id),
                  })}
                </span>
              </button>
            ))}
          </div>
        )}
        {modern && markets.length > 1 && (
          <div className={styles.marketChips}>
            {markets.map((m) => (
              <button
                type="button"
                key={m.key}
                className={classNames(
                  styles.marketChip,
                  m.key === currentMarket.key && styles.marketChipOn
                )}
                onClick={() => setMarket(m.key)}
              >
                {tabLabel(m, current, t)}
              </button>
            ))}
          </div>
        )}
      </div>
      {layout === 'vip' && (
        <VipLiveHero
          events={heroEvents}
          favorites={favoriteEvents}
          loading={!liveReady}
        />
      )}
      {favoriteEvents.length > 0 && (
        <LiveEventsList events={favoriteEvents} favorite={true} sportId={0} />
      )}
      {modern &&
        shown.map((x) =>
          hasRows(x) ? (
            <LiveEventsList
              events={rowsOf(x)}
              sportId={x}
              key={x}
              bare
              market={currentMarket}
            />
          ) : (
            <LiveRowsSkeleton key={x} />
          )
        )}
      {/* Classic: "All" lists every sport one after the other, each with
          its header, its own odds and its matches; a sport on the rail
          lists that sport alone. */}
      {!modern && sportIds.length > 1 && (
        <ClassicSportRail
          sportIds={sportIds}
          count={countOf}
          picked={classicSport}
          onPick={setClassicSport}
        />
      )}
      {!modern &&
        classicShown.map((x) => (
          <ClassicSportSection
            key={x}
            sportId={x}
            events={rowsOf(x)}
            count={countOf(x)}
            waiting={!hasRows(x)}
          />
        ))}
      {outlineOnly && <LiveRowsSkeleton />}
    </>
  );
};

/** Football, basketball and tennis first, the rest in the feed's order
 *  (owner, 2026-10-30). The order is fixed, whichever list came first. */
const LEAD_SPORTS = [SportIDs.SOCCER, SportIDs.BASKETBALL, SportIDs.TENNIS];
const sportRank = (id: number) => {
  const i = LEAD_SPORTS.indexOf(id);
  return i < 0 ? LEAD_SPORTS.length + LIVE_SPORT_IDs.indexOf(id) : i;
};

/** Classic: the sports filter on a rail that stays on top: "All" first,
 *  then each sport with its number of matches; the picked one stands out. */
const ClassicSportRail = ({
  sportIds,
  count,
  picked,
  onPick,
}: {
  sportIds: number[];
  count: (id: number) => number;
  picked: number | null;
  onPick: (id: number | null) => void;
}) => {
  const { t } = useTranslation();
  const rail = useRef<HTMLDivElement>(null);
  const active = picked !== null && sportIds.includes(picked) ? picked : null;
  const total = sportIds.reduce((n, id) => n + count(id), 0);

  // Keep the picked pill in sight on the rail.
  useEffect(() => {
    rail.current
      ?.querySelector<HTMLElement>(`[data-sport="${active ?? 'all'}"]`)
      ?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [active]);

  // After a pick, the list starts again just under the rail (it was maybe
  // scrolled deep into the longer list it replaces).
  const picks = useRef(0);
  const pick = (id: number | null) => {
    picks.current += 1;
    onPick(id);
  };
  useEffect(() => {
    const box = rail.current;
    if (!picks.current || !box) return;
    const next = box.nextElementSibling as HTMLElement | null;
    if (!next) return;
    const under =
      parseFloat(getComputedStyle(box).top || '0') + box.offsetHeight;
    const by = next.getBoundingClientRect().top - under;
    if (by >= 0) return;
    // The page's own scroller: the nearest ancestor that scrolls.
    let el: HTMLElement | null = box.parentElement;
    while (el && el !== document.body) {
      const o = getComputedStyle(el).overflowY;
      if ((o === 'auto' || o === 'scroll') && el.scrollHeight > el.clientHeight)
        break;
      el = el.parentElement;
    }
    if (el && el !== document.body) el.scrollBy({ top: by });
    else window.scrollBy({ top: by });
  }, [active]);

  const pill = (
    key: string,
    on: boolean,
    onClick: () => void,
    content: ReactNode,
    n: number
  ) => (
    <button
      type="button"
      key={key}
      data-sport={key}
      className={classNames(styles.sportPill, on && styles.sportPillOn)}
      aria-pressed={on}
      onClick={onClick}
    >
      {content}
      <span className={styles.sportPillCount}>{n}</span>
    </button>
  );

  return (
    <div className={styles.sportRail} ref={rail}>
      {pill(
        'all',
        active === null,
        () => pick(null),
        <span>{t('live_all_sports')}</span>,
        total
      )}
      {sportIds.map((id) =>
        pill(
          String(id),
          id === active,
          () => pick(id),
          <>
            <SportIcon
              sportId={id}
              className={styles.sportPillIcon}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = getDefaultSportIconSrc();
              }}
            />
            <span>
              {t(sportTranslationKey(id), {
                defaultValue: getLiveBettingSportName(id),
              })}
            </span>
          </>,
          count(id)
        )
      )}
    </div>
  );
};

/** Classic: one sport's part of the live list - its header with a small
 *  list to pick the odds its rows show (the sport's important markets, as
 *  swisslos.ch), and the matches. */
const ClassicSportSection = ({
  sportId,
  events,
  count,
  waiting,
}: {
  sportId: number;
  events: LiveEvent[];
  count: number;
  /** Its list has not come yet. */
  waiting: boolean;
}) => {
  const { t } = useTranslation();
  const [market, setMarket] = useState<LiveMarketKey>('full');
  const markets = liveMarketsOn(
    sportId,
    events,
    LIVE_FULL_TIME_BET_MAP.get(sportId)
  );
  const current = markets.find((m) => m.key === market) ?? markets[0];
  return (
    <div id={`live-sport-${sportId}`} className={styles.classicSection}>
      <div className={styles.classicSectionHead}>
        <SportIcon
          sportId={sportId}
          className={styles.classicIcon}
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = getDefaultSportIconSrc();
          }}
        />
        <span className={styles.classicSectionName}>
          {t(sportTranslationKey(sportId), {
            defaultValue: getLiveBettingSportName(sportId),
          })}
          <span className={styles.classicSectionCount}>{count}</span>
        </span>
        {markets.length > 1 && (
          <MarketPicker
            markets={markets}
            current={current}
            label={(m) => tabLabel(m, sportId, t)}
            onPick={(m) => setMarket(m.key)}
          />
        )}
      </div>
      {waiting ? (
        <LiveRowsSkeleton />
      ) : (
        <LiveEventsList
          events={events}
          sportId={sportId}
          bare
          market={current}
        />
      )}
    </div>
  );
};

/** A small dropdown on the sport's header line: the picked market on a
 *  button, the others in a list that opens just under it (not the phone's
 *  full-screen picker), with a search on top and a blinking arrow while
 *  there is more below; closes on a pick or a tap elsewhere. Also the VIP
 *  league page's market list (owner, 2026-11-02: "kao na uživo"). */
export const MarketPicker = <M extends { key: string }>({
  markets,
  current,
  label,
  onPick,
}: {
  markets: M[];
  current: M;
  label: (m: M) => string;
  onPick: (m: M) => void;
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [more, setMore] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const q = query.trim().toLowerCase();
  const shown = q
    ? markets.filter((m) => label(m).toLowerCase().includes(q))
    : markets;
  // Something below the list's edge: show the arrow.
  const checkMore = () => {
    const el = list.current;
    setMore(!!el && el.scrollTop + el.clientHeight < el.scrollHeight - 4);
  };
  useEffect(() => {
    if (!open) return;
    checkMore();
    const away = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [open, shown.length]);
  return (
    <div className={styles.picker} ref={box}>
      <button
        type="button"
        className={classNames(styles.pickerButton, open && styles.pickerOpen)}
        aria-label={t('live_market_pick')}
        aria-expanded={open}
        onClick={() => {
          setOpen(!open);
          setQuery('');
        }}
      >
        <span>{label(current)}</span>
        <span className={styles.pickerChevron} aria-hidden="true" />
      </button>
      {open && (
        <div className={styles.pickerPanel}>
          {markets.length > 6 && (
            <input
              className={styles.pickerSearch}
              type="search"
              value={query}
              placeholder={t('search_label')}
              onChange={(e) => setQuery(e.target.value)}
            />
          )}
          <ul
            className={styles.pickerList}
            role="listbox"
            ref={list}
            onScroll={checkMore}
          >
            {shown.map((m) => (
              <li
                key={m.key}
                role="option"
                aria-selected={m.key === current.key}
                className={classNames(
                  styles.pickerItem,
                  m.key === current.key && styles.pickerItemOn
                )}
                onClick={() => {
                  onPick(m);
                  setOpen(false);
                }}
              >
                {label(m)}
              </li>
            ))}
            {shown.length === 0 && <li className={styles.pickerEmpty}>—</li>}
          </ul>
          {more && <span className={styles.pickerMore} aria-hidden="true" />}
        </div>
      )}
    </div>
  );
};
